/**
 * Platform detection utilities for tracking without Ionic dependency
 * Re-exports functions from @allie/app-ui-lib to avoid duplication
 */

// Re-export platform detection functions from ui-lib
export { getOSVersion, getDeviceModel } from '@allie/app-ui-lib';

export enum MobileTrackingEvents {
  APP_PAGE_VIEW = 'app_page_view',
  USER_LOGIN = 'user_login',
  APP_SESSION = 'app_session',
  FACTORYGPT_PROMPT_SENT = 'factorygpt_prompt_sent',
}

export enum TrackingPlatform {
  APP_IOS = 'App iOS',
  APP_ANDROID = 'App Android',
  WEB_DESKTOP = 'Web Desktop',
  WEB_MOBILE = 'Web Mobile',
}

interface PlatformInfo {
  platform: TrackingPlatform;
  isAppMobile: boolean;
  isWeb: boolean;
}

/**
 * Get the platform string for Amplitude tracking events
 */
export const getTrackingPlatform = (): PlatformInfo => {
  // Check if running in Capacitor (native app)
  const isCapacitor = !!(window as any).Capacitor;

  // Detect platforms using user agent
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIOS =
    /iphone|ipad|ipod/.test(userAgent) || (/macintosh/.test(userAgent) && 'ontouchend' in document);
  const isAndroid = /android/.test(userAgent);
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(
    userAgent
  );
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth < 768;
  const isMobileWeb = isMobileUA || (hasTouch && isSmallScreen);

  // Native mobile apps via Capacitor
  if (isCapacitor) {
    if (isIOS) {
      return {
        platform: TrackingPlatform.APP_IOS,
        isAppMobile: true,
        isWeb: false,
      };
    }
    if (isAndroid) {
      return {
        platform: TrackingPlatform.APP_ANDROID,
        isAppMobile: true,
        isWeb: false,
      };
    }
  }

  // Web platforms
  if (isMobileWeb && !isCapacitor) {
    return {
      platform: TrackingPlatform.WEB_MOBILE,
      isAppMobile: false,
      isWeb: true,
    };
  }

  // Default to Web Desktop
  return {
    platform: TrackingPlatform.WEB_DESKTOP,
    isAppMobile: false,
    isWeb: true,
  };
};

/**
 * Get app version for tracking
 */
export const getAppVersion = (): string => {
  return process.env.REACT_APP_VERSION || 'unknown';
};
